import React from "react";
import { useTranslation } from "react-i18next";
import LegalPageShell from "../../components/LegalPageShell";

export default function Security() {
  const { t } = useTranslation(["legalSecurity", "common", "legal"]);

  const sections = [
    { id: "introduction", label: t("introduction", "Introduction") },
    { id: "security-model", label: t("securityModel", "Security Model") },
    { id: "authentication", label: t("authenticationControls", "Authentication Controls") },
    { id: "authorization", label: t("authorizationAndDataAccess", "Authorization and Data Access") },
    { id: "transport-storage", label: t("transportAndStorageSecurity", "Transport and Storage Security") },
    { id: "incident-response", label: t("moderationAbuseAndIncidentHandling", "Moderation, Abuse, and Incident Handling") },
    { id: "user-responsibilities", label: t("userSecurityResponsibilities", "User Security Responsibilities") },
    { id: "disclosure", label: t("responsibleDisclosure", "Responsible Disclosure") },
  ];

  return (
<LegalPageShell
        title={t("title", "Security Policy")}
        subtitle={t("subtitle", "Orrith security practices, vulnerability reporting, and infrastructure protection.")}
        effectiveDate={`Effective: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`}
        sections={sections}
        namespace="legalSecurity"
      >
      <section id="introduction" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("introduction", "Introduction")}
        </h2>
        <p className="legal-prose">
          {t("securityIntro", "Orrith takes the security of your data and our platform seriously. This page describes the security controls and operational safeguards currently implemented in Orrith at orrith.me. We continuously review and improve our security posture to protect against evolving threats.")}
        </p>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("atAGlance", "At a Glance")}
        </h3>
        <div className="grid gap-4 sm:grid-cols-3 mt-4">
          <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-gray-50 dark:bg-neutral-900/40">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-1.5">
              {t("identityControls", "Identity Controls")}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("identityControlsDesc", "Authentication flows and protected route checks help prevent unauthorized access.")}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-gray-50 dark:bg-neutral-900/40">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-1.5">
              {t("accessControls", "Access Controls")}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("accessControlsDesc", "Firestore security rules and role-based controls limit who can read and modify data.")}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-gray-50 dark:bg-neutral-900/40">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-1.5">
              {t("incidentResponse", "Incident Response")}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("incidentResponseDesc", "Moderation and support workflows provide paths to report abuse and investigate issues.")}
            </p>
          </div>
        </div>
      </section>

      <section id="security-model" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("securityModel", "Security Model")}
        </h2>
        <p className="legal-prose">
          {t("securityModelDesc", "Orrith uses Firebase-based authentication, data access rules, and role-aware administrative controls to protect account data and application operations. Security enforcement is implemented at both client flow and Firestore authorization layers. Our security model follows the principle of least privilege: users and systems have only the minimum access necessary to perform their functions.")}
        </p>
      </section>

      <section id="authentication" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("authenticationControls", "Authentication Controls")}
        </h2>
        <p className="legal-prose mb-6">
          {t("authIntro", "Orrith implements the following authentication controls to verify user identity:")}
        </p>
        <ul className="legal-list">
          <li>{t("authControls.emailPassword", "Email/password authentication and Google sign-in are supported")}</li>
          <li>{t("authControls.phoneVerification", "Optional phone verification flow is available when enabled by environment configuration")}</li>
          <li>{t("authControls.emailVerification", "Email verification checks are enforced for selected protected routes")}</li>
          <li>{t("authControls.passwordReset", "Password reset and verification email flows are supported through Firebase Authentication")}</li>
        </ul>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("authSessionManagement", "Session Management")}
        </h3>
        <ul className="legal-list">
          <li>{t("authSession.tokens", "Firebase ID tokens are used for authenticated requests and automatically refreshed")}</li>
          <li>{t("authSession.multipleTabs", "Multiple browser tabs share authentication state through Firebase Auth persistence")}</li>
          <li>{t("authSession.logout", "Sign out clears local authentication state and revokes server-side tokens where supported")}</li>
          <li>{t("authSession.reauth", "Sensitive operations (account deletion, email change) require recent authentication")}</li>
        </ul>
      </section>

      <section id="authorization" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("authorizationAndDataAccess", "Authorization and Data Access")}
        </h2>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("firestoreRuleEnforcement", "Firestore Rule Enforcement")}
        </h3>
        <p className="legal-prose mb-4">
          {t("firestoreIntro", "All data access is controlled through Firebase Security Rules applied at the database layer:")}
        </p>
        <ul className="legal-list">
          <li>{t("firestoreRules.collectionLevel", "Collection-level read and write restrictions by authenticated identity")}</li>
          <li>{t("firestoreRules.participantChecks", "Participant checks on chat and message resources")}</li>
          <li>{t("firestoreRules.ownerConstraints", "Owner/admin constraints on updates and deletes for core records")}</li>
          <li>{t("firestoreRules.defaultDeny", "Default deny-all fallback for unrecognized document paths")}</li>
        </ul>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("adminRoleControls", "Admin Role Controls")}
        </h3>
        <p className="legal-prose mb-4">
          {t("adminIntro", "Administrative operations are gated by role fields in user records:")}
        </p>
        <ul className="legal-list">
          <li>{t("adminControls.roleFields", "Role fields in user records gate administrative operations")}</li>
          <li>{t("adminControls.moderationRestricted", "Restricted moderation, ticket, and feedback actions for admins")}</li>
          <li>{t("adminControls.superadminOnly", "Superadmin-only constraints for sensitive role transitions")}</li>
          <li>{t("adminControls.activityLogging", "Admin activity logging for operational traceability")}</li>
        </ul>
      </section>

      <section id="transport-storage" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("transportAndStorageSecurity", "Transport and Storage Security")}
        </h2>
        <ul className="legal-list">
          <li>{t("transportSecurity.https", "Browser-to-service communication occurs over HTTPS endpoints")}</li>
          <li>{t("transportSecurity.firebaseStorage", "Persistent platform data is stored in Firebase-managed services with encryption at rest")}</li>
          <li>{t("transportSecurity.clientCache", "Client-side cached data is stored in browser storage on the user device")}</li>
          <li>{t("transportSecurity.noCardStorage", "No live in-app card payment credential storage is implemented in current payment modal flows")}</li>
        </ul>
      </section>

      <section id="incident-response" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("moderationAbuseAndIncidentHandling", "Moderation, Abuse, and Incident Handling")}
        </h2>
        <p className="legal-prose mb-6">
          {t("incidentIntro", "Orrith provides multiple layers of protection against abuse and security incidents:")}
        </p>
        <ul className="legal-list">
          <li>{t("moderationActions.contentFlagged", "Content may be flagged and reviewed through moderation tooling")}</li>
          <li>{t("moderationActions.supportTickets", "Support tickets and feedback reports are available for issue escalation")}</li>
          <li>{t("moderationActions.errorReporting", "Runtime error reporting allows users to submit reproducible issue context")}</li>
          <li>{t("moderationActions.adminWorkflows", "Administrative review workflows support status updates and follow-up actions")}</li>
        </ul>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("incidentProcess", "Incident Response Process")}
        </h3>
        <ul className="legal-list">
          <li>{t("incidentProcessItems.detection", "Detection: Automated monitoring and user reports trigger investigation")}</li>
          <li>{t("incidentProcessItems.triage", "Triage: Security team assesses severity and impact")}</li>
          <li>{t("incidentProcessItems.containment", "Containment: Immediate actions to limit exposure")}</li>
          <li>{t("incidentProcessItems.resolution", "Resolution: Root cause analysis and remediation")}</li>
          <li>{t("incidentProcessItems.communication", "Communication: Affected users notified as required by law and policy")}</li>
        </ul>
      </section>

      <section id="user-responsibilities" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("userSecurityResponsibilities", "User Security Responsibilities")}
        </h2>
        <p className="legal-prose mb-6">
          {t("userSecurityIntro", "Security is a shared responsibility. You can help protect your account by:")}
        </p>
        <ul className="legal-list">
          <li>{t("userSecurity.strongCredentials", "Use strong, unique credentials and protect account access")}</li>
          <li>{t("userSecurity.verifyUrls", "Verify destination URLs and avoid sharing account credentials")}</li>
          <li>{t("userSecurity.reviewSettings", "Review posted content and location-sharing settings before publishing")}</li>
          <li>{t("userSecurity.reportActivity", "Report suspicious activity through available support and reporting channels")}</li>
          <li>{t("userSecurity.softwareUpdates", "Keep your browser and device software updated")}</li>
          <li>{t("userSecurity.phishing", "Be cautious of phishing attempts; Orrith will never ask for your password via email")}</li>
        </ul>
      </section>

      <section id="disclosure" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("responsibleDisclosure", "Responsible Disclosure")}
        </h2>
        <p className="legal-prose mb-4">
          {t("responsibleDisclosureDesc", "If you identify a security weakness, report it through official support channels with clear reproduction details. Do not exploit vulnerabilities, access data without permission, or perform disruptive testing on live users.")}
        </p>
        <p className="legal-prose mb-4">
          {t("disclosureEmail", "Security issues can be reported to security@orrith.me. We aim to acknowledge reports within 48 hours and provide a preliminary assessment within 5 business days.")}
        </p>
        <ul className="legal-list">
          <li>{t("disclosure.scope", "Test only on your own accounts and data")}</li>
          <li>{t("disclosure.noDisruption", "Do not perform actions that could disrupt service for other users")}</li>
          <li>{t("disclosure.noDataAccess", "Do not access, modify, or delete data belonging to others")}</li>
          <li>{t("disclosure.coordinate", "Coordinate with us before public disclosure to allow time for remediation")}</li>
        </ul>
      </section>
    </LegalPageShell>
  );
}